import { type AxiosInstance } from 'axios';

import { type CourseHoleDetail } from '@api/courses-api.ts';
import { type SortOrder } from '@assets/enums';

import { type DeviceStatus, type DeviceTypeEnum } from '../assets/enums/device-type.emun.ts';
import { prepareOrderingForBackend } from '../assets/helpers/utils/prepare-ordering-for-backend.ts';
import { type ChallengeConfig } from './challenge-configs-api.ts';
import { type DrivingRangeBayPosition } from './driving-range-types.ts';
import type {
  CommonListArgs,
  CourseShort,
  FirmwareShort,
  GetEntityByIdParams,
  Gps,
  HoleShort,
  IsoDate,
  Nullable,
  PaginatedList,
  SortListArgs,
  UUID,
} from './types.ts';

export class DevicesApi {
  constructor(private instance: AxiosInstance) {}

  public async getDevices(params: DevicesListArgs) {
    return this.instance
      .get<PaginatedList<DeviceListItem>>('/devices/', {
        params: {
          page: params.page,
          search: params.search,
          ordering: prepareOrderingForBackend(params.sortBy, params.sortDirection),
          state: params.state,
          device_type: params.device_type,
          country_id: params.country_id,
          course_id: params.course_id,
          firmware_id: params.firmware_id,
          unlinked: params.unlinked,
        },
      })
      .then((res) => res.data);
  }

  public async getDeviceById(params: GetEntityByIdParams) {
    return this.instance.get<DeviceDetail>(`/devices/${params.id}/`).then((res) => res.data);
  }

  /** Loads the latest presence reading using the device UUID. */
  public async getDevicePresence({ deviceId }: DevicePresenceParams) {
    return this.instance
      .get<DevicePresenceResponse>(`/devices/${encodeURIComponent(deviceId)}/presence`, {
        errorHandledLocally: true,
      })
      .then((res) => res.data);
  }

  /** Sends a wake-up command using the device UUID. */
  public async wakeUpDevice(params: DevicePowerParams) {
    return this.instance
      .post<void>('/devices/wake-up', {
        device_id: params.deviceId,
      })
      .then((res) => res.data);
  }

  /** Requests sleep; completion is reported by the device state. */
  public async sleepDevice(params: DevicePowerParams) {
    const deviceId = encodeURIComponent(params.deviceId);

    return this.instance.post<void>(`/devices/${deviceId}/sleep`).then((res) => res.data);
  }

  public async getDeviceMetricsCube(params: DeviceMetricsCubeParams) {
    const deviceId = encodeURIComponent(params.deviceId);

    return this.instance
      .get<DeviceMetricsCube>(`/device-metrics/${deviceId}/cube`, {
        params: {
          hours: params.hours,
          resolution: params.resolution,
        },
      })
      .then((res) => res.data);
  }

  public async getDeviceMetricsCubeLatest(params: DeviceMetricsCubeLatestParams) {
    const deviceId = encodeURIComponent(params.deviceId);

    return this.instance
      .get<DeviceMetricsCube>(`/device-metrics/${deviceId}/cube/latest`, {
        params: {
          since: params.since,
        },
      })
      .then((res) => res.data);
  }

  public async getDeviceLogs(params: DeviceLogListArgs) {
    return this.instance
      .get<DeviceLogsListResponse>('/device-logs', {
        params: {
          page: params.page,
          page_size: params.pageSize,
          by: params.by,
          value: params.value,
        },
      })
      .then((res) => res.data);
  }

  public async getFirmwares(params: FirmwaresListArgs) {
    return this.instance.get<Firmware[]>('/firmware/', { params }).then((res) => res.data);
  }

  public async setFirmwareVersion(params: SetFirmwareVersionParams) {
    return this.instance
      .post<void>('/firmware/assign/', {
        firmware_id: params.firmwareId,
        device_id: params.deviceId,
      })
      .then((res) => res.data);
  }

  public async startCameraDewarp(params: CameraDewarpParams) {
    const deviceId = encodeURIComponent(params.deviceId);
    const cameraName = encodeURIComponent(params.cameraName);

    return this.instance
      .post<CameraDewarpResponse>(`/devices/${deviceId}/cameras/${cameraName}/dewarp`)
      .then((res) => res.data);
  }

  public async stopCameraDewarp(params: CameraDewarpParams) {
    const deviceId = encodeURIComponent(params.deviceId);
    const cameraName = encodeURIComponent(params.cameraName);

    return this.instance
      .delete<CameraDewarpResponse>(`/devices/${deviceId}/cameras/${cameraName}/dewarp`)
      .then((res) => res.data);
  }

  /** Starts annotated detection for the selected device camera. */
  public async startCameraDetection(params: CameraDetectionParams) {
    const deviceId = encodeURIComponent(params.deviceId);
    const cameraName = encodeURIComponent(params.cameraName);

    return this.instance
      .post<void>(`/devices/${deviceId}/cameras/${cameraName}/detection`)
      .then((res) => res.data);
  }

  /** Stops detection for the selected device camera. */
  public async stopCameraDetection(params: CameraDetectionParams) {
    const deviceId = encodeURIComponent(params.deviceId);
    const cameraName = encodeURIComponent(params.cameraName);

    return this.instance
      .delete<void>(`/devices/${deviceId}/cameras/${cameraName}/detection`)
      .then((res) => res.data);
  }

  /** Updates only supplied overlay settings; the backend restarts active detection. */
  public async updateCameraOverlay(params: CameraOverlayParams) {
    const deviceId = encodeURIComponent(params.deviceId);
    const cameraName = encodeURIComponent(params.cameraName);

    return this.instance
      .put<void>(`/devices/${deviceId}/cameras/${cameraName}/overlay`, params.body)
      .then((res) => res.data);
  }

  /** Queues recording through the authenticated Portal API; an empty 200 acknowledges acceptance. */
  public async startDeviceRecord(params: DeviceRecordParams) {
    const deviceId = encodeURIComponent(params.deviceId);

    return this.instance.post<void>(`/devices/${deviceId}/record/start`).then((res) => res.data);
  }

  /** Queues stopping on-device recording without affecting streaming. */
  public async stopDeviceRecord(params: DeviceRecordParams) {
    const deviceId = encodeURIComponent(params.deviceId);

    return this.instance.post<void>(`/devices/${deviceId}/record/stop`).then((res) => res.data);
  }

  /** Starts the device switcher; a running process does not guarantee video readiness. */
  public async startDeviceSwitcher(params: DeviceSwitcherStartParams) {
    const deviceId = encodeURIComponent(params.deviceId);

    return this.instance
      .post<DeviceSwitcherResponse>(`/devices/${deviceId}/switcher/start`, {
        initial: params.initial,
      })
      .then((res) => res.data);
  }

  /** Stops the device-level ML switcher. */
  public async stopDeviceSwitcher(params: DeviceSwitcherParams) {
    const deviceId = encodeURIComponent(params.deviceId);

    return this.instance
      .post<DeviceSwitcherResponse>(`/devices/${deviceId}/switcher/stop`)
      .then((res) => res.data);
  }
}

export type DevicePresenceParams = {
  deviceId: UUID;
};

// Presence and target fields are optional in the OpenAPI response schema.
export type DevicePresenceTarget = {
  x_mm?: number;
  y_mm?: number;
  speed_cm_s?: number;
  distance_gate_mm?: number;
};

export type DevicePresence = {
  available?: boolean;
  detected?: boolean;
  observed_at?: IsoDate;
  received_at?: IsoDate;
  session_id?: Nullable<string>;
  target_count?: number;
  targets?: DevicePresenceTarget[];
};

export type DevicePresenceResponse = {
  device_id: UUID;
  presence: Nullable<DevicePresence>;
};

export type DevicePowerParams = {
  /** Device UUID from the device's id field. */
  deviceId: UUID;
};

export type CameraDetectionParams = {
  deviceId: UUID;
  cameraName: string;
};

export type DistanceGrid = {
  show_grid: boolean;
  show_trail: boolean;
};

export type CameraOverlayParams = CameraDetectionParams & {
  body: Partial<DistanceGrid>;
};

export type DeviceRecordParams = {
  /** Device UUID used by the Portal recording endpoints. */
  deviceId: UUID;
};

export type DeviceSwitcherParams = {
  deviceId: UUID;
};

export type DeviceSwitcherStartParams = DeviceSwitcherParams & {
  /** Initial camera name; does not change an already running switcher. */
  initial?: string;
};

export type DeviceSwitcherResponse = {
  device_id: UUID;
  /** Describes the switcher process, not video readiness. */
  running: boolean;
};

export type SetFirmwareVersionParams = {
  firmwareId: string;
  deviceId: string;
};

export type CameraDewarpParams = {
  deviceId: UUID;
  cameraName: string;
};

export type CameraDewarpStream = {
  name: string;
  dewarp_hls_url?: string;
  dewarp_webrtc_url?: string;
};

export type CameraDewarpMetadata = {
  cameras: CameraDewarpStream[];
};

export type CameraDewarpResponse = {
  metadata: CameraDewarpMetadata;
};

export type FirmwaresListArgs = {
  // Sort order by date (ASC — oldest first, DESC — newest first)
  order: SortOrder;
};

export type Firmware = {
  id: string;
  version: string;
  uuid: string;
  type: DeviceTypeEnum;
  date: IsoDate;
  devices_count: number;
};

export type DeviceDetail = {
  id: UUID;
  name: string;
  serial_number: string;
  device_type: DeviceTypeEnum;
  hwid: Nullable<string>;
  firmware: Nullable<FirmwareShort>;
  last_heartbeat: Nullable<IsoDate>;
  last_stream_start: Nullable<IsoDate>;
  updated_at: Nullable<IsoDate>;
  gps: Gps;
  state: DeviceStatus;
  cameras: DeviceCamera[];
  switcher_whep_url: Nullable<string>;
  partner: Nullable<DevicePartner>;
  course: Nullable<CourseShort>;
  mac: Nullable<string>;
  meshcentral: Nullable<string>;
  hole: Nullable<CourseHoleDetail>;
  driving_range: Nullable<DeviceDrivingRange>;
  bay: Nullable<DeviceBay>;
};

export type DeviceDrivingRange = {
  id: number;
  course: string;
  created_at: IsoDate;
  updated_at: IsoDate;
};

export type DeviceBay = {
  id: number;
  floor: number;
  /** One-based position among bays on the same floor, ordered by ID. */
  number: number;
  challenge_configs: ChallengeConfig[];
};

export type DevicePartner = {
  id: UUID;
  name: string;
};

export type DeviceCamera = {
  name: string;
  type: string;
  width: Nullable<number>;
  height: Nullable<number>;
  fps: Nullable<number>;
  bitrate: Nullable<number>;
  status: Nullable<DeviceStatus.Online | DeviceStatus.Offline>;
  use_h264_stream: Nullable<boolean>;
  rtmp_url: Nullable<string>;
  hls_url: Nullable<string>;
  webrtc_url: Nullable<string>;
  dewarp_enabled: Nullable<boolean>;
  dewarp_rtmp_url: Nullable<string>;
  dewarp_hls_url: Nullable<string>;
  dewarp_webrtc_url: Nullable<string>;
  dewarp_started_at: Nullable<IsoDate>;
  dewarp_stopped_at: Nullable<IsoDate>;
  detection_whep_url: Nullable<string>;
  distance_grid: DistanceGrid;
  last_activity: Nullable<IsoDate>;
};

export type DevicesListArgs = {
  search?: string;
  state?: DeviceStatus;
  device_type?: DeviceTypeEnum;
  country_id?: number;
  course_id?: string;
  firmware_id?: string;
  unlinked?: boolean;
} & CommonListArgs &
  SortListArgs;

export type DeviceListItem = {
  bay: Nullable<DrivingRangeBayPosition>;
  id: UUID;
  name: string;
  serial_number: string;
  device_type: DeviceTypeEnum;
  hwid: Nullable<string>;
  state: DeviceStatus;
  mac_address: Nullable<string>;
  course: Nullable<CourseShort>;
  hole: Nullable<HoleShort>;
  gps: Gps;
  battery: Nullable<number>;
  temperature: Nullable<number>;
  last_heartbeat: Nullable<IsoDate>;
  last_stream_start: Nullable<IsoDate>;
  firmware: Nullable<FirmwareShort>;
  updated_at: Nullable<IsoDate>;
};

export type DeviceLogLevel = 'INFO' | 'ALERT' | 'WARNING' | 'ERROR';

export type DeviceLogCategory =
  | 'STARTUP'
  | 'STREAMING'
  | 'SENSORS'
  | 'RECORDING'
  | 'POWER'
  | 'CAMERA'
  | 'MOVEMENT'
  | 'NETWORK'
  | 'FIRMWARE';

export type DeviceLogListArgs = {
  page: number;
  pageSize?: number;
  by?: 'all' | 'device' | 'hole';
  /** Device UUID for by=device; Portal hole UUID for by=hole. */
  value?: UUID;
};

export type DeviceLog = {
  id: string;
  device_type: DeviceTypeEnum;
  category: DeviceLogCategory;
  code: string;
  message: string;
  context?: Nullable<Record<string, unknown>>;
  level: DeviceLogLevel;
  date: IsoDate;
  created_at: IsoDate;
  updated_at: IsoDate;
};

export type DeviceLogsListResponse = PaginatedList<DeviceLog>;

export type DeviceMetricResolution = '10s' | '1m' | '5m' | '15m' | '30m' | '1h';

export type DeviceMetricsCubeParams = {
  deviceId: UUID;
  hours: number;
  resolution: DeviceMetricResolution;
};

export type DeviceMetricsCubeLatestParams = {
  deviceId: UUID;
  since?: IsoDate;
};

export type DeviceMetricVector = {
  x: Nullable<number>;
  y: Nullable<number>;
  z: Nullable<number>;
};

export type DeviceMetricBucket<TValue> = {
  bucket_start: IsoDate;
  avg: TValue;
  min: TValue;
  max: TValue;
  count: number;
};

export type DeviceMetricScalarBucket = DeviceMetricBucket<Nullable<number>>;
export type DeviceMetricVectorBucket = DeviceMetricBucket<DeviceMetricVector>;

export type DeviceMetricNetworkSignal = {
  avg: number;
  min: number;
  max: number;
  count: number;
};

export type DeviceMetricSignalQualityBucket = DeviceMetricBucket<Nullable<number>> & {
  net_types?: Record<string, DeviceMetricNetworkSignal>;
};

export type DeviceMetricsCube = {
  device_id: UUID;
  resolution: DeviceMetricResolution;
  hours: number;
  metrics: {
    activity: DeviceMetricScalarBucket[];
    temperature: DeviceMetricScalarBucket[];
    battery: DeviceMetricScalarBucket[];
    data_transfer: DeviceMetricScalarBucket[];
    power: DeviceMetricScalarBucket[];
    cpu_usage: DeviceMetricScalarBucket[];
    ram_usage: DeviceMetricScalarBucket[];
    magnetic_heading: DeviceMetricScalarBucket[];
    magnetic_magnitude: DeviceMetricScalarBucket[];
    tof_distance: DeviceMetricScalarBucket[];
    ambient_light: DeviceMetricScalarBucket[];
    signal_quality: DeviceMetricSignalQualityBucket[];
    temperature_sensor: DeviceMetricScalarBucket[];
    temperature_power: DeviceMetricScalarBucket[];
    temperature_camera: DeviceMetricScalarBucket[];
    temperature_cpu: DeviceMetricScalarBucket[];
    acceleration: DeviceMetricVectorBucket[];
    gyroscope: DeviceMetricVectorBucket[];
    magnetic_field: DeviceMetricVectorBucket[];
  };
};
